import { AmazonRdsSqlServerTableDataset } from 'app/model/dataset/amazon-rds-sql-server-table.dataset';
import {
    AzureSqlDatabaseLinkedService,
    AzureSqlDWTableDataset,
    ConnectionKind,
    Dataset,
    FormatDatasetV2,
    IResourceContext,
    LinkedService,
    Metamodel,
    SnowflakeLinkedService,
    SqlServerTableDataset,
    TabularTypeDataset
} from 'shell-models-lib';
import { AzureSqlTableDataset } from 'app/model/dataset/azure-sql-table.dataset';
import { AzureSqlMITableDataset } from 'app/model/dataset/azure-sql-mi-table.dataset';
import { isFileTypeCategoryDataset, isFileTypeDataset } from 'app/shared/utils/dataset-validation.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { QueryApiFactoryService, type FabricCaller } from 'app/apis/query-api.factory.service';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { IQueryParameterInfo } from 'app/apis/query-api.models';
import { compact } from 'lodash';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { GlobalVariables } from 'app/services/globals';

type SqlBasedDataset =
    | SqlServerTableDataset
    | AmazonRdsSqlServerTableDataset
    | AzureSqlTableDataset
    | AzureSqlMITableDataset;

const isSqlBasedDataflowDataset = (
    dataset: Dataset
): dataset is Exclude<SqlBasedDataset, AmazonRdsSqlServerTableDataset> => {
    // Amazon RDS for SQL Server doesn't support dataflow. When AmazonRdsSqlServerTableDataset inherits
    // SqlServerTableDataset, we need to change it.

    // AzureSqlMITableDataset inherits AzureSqlTableDataset, so it's also included
    return (
        dataset instanceof AzureSqlTableDataset ||
        dataset instanceof SqlServerTableDataset ||
        dataset instanceof AzureSqlDWTableDataset
    );
};

const isSqlBasedDataset = (dataset: Dataset): dataset is SqlBasedDataset => {
    // AzuerSqlMITableDataset is also included
    return (
        dataset instanceof AzureSqlTableDataset ||
        dataset instanceof SqlServerTableDataset ||
        // note: when AmazonRdsSqlServerTableDataset inherits SqlServerTableDataset, remove next line
        dataset instanceof AmazonRdsSqlServerTableDataset
    );
};

const isSqlBasedDatasetType = (datasetType: string): boolean => {
    return (
        datasetType === Metamodel.modelFor(AzureSqlTableDataset).name ||
        datasetType === Metamodel.modelFor(SqlServerTableDataset).name ||
        datasetType === Metamodel.modelFor(AmazonRdsSqlServerTableDataset).name ||
        datasetType === Metamodel.modelFor(AzureSqlMITableDataset).name
    );
};

const isSqlServer = (dataset: Dataset, inherit: boolean = true): dataset is SqlServerTableDataset => {
    return inherit
        ? dataset instanceof SqlServerTableDataset
        : Metamodel.modelOf(dataset).name === Metamodel.modelFor(SqlServerTableDataset).name;
};

const isFileBasedTypeDataset = (dataset: Dataset, services: { dsSvc: DatasetRegistryService }): boolean => {
    return isFileTypeDataset(dataset, services) || isFileTypeCategoryDataset(dataset, services);
};

// This function can list the databases in Snowflake by default, but it can also list the storage integrations if the isDatabase parameter is set to false.
const listSnowflakeData = async (
    contextData: IResourceContext,
    linkedService: SnowflakeLinkedService,
    isDatabase: boolean = true,
    parameterInfo?: IQueryParameterInfo,
    connectionProperties?: { [key: string]: string },
    caller?: FabricCaller | null
): Promise<Array<{ name: string; kind?: 'STANDARD' | 'IMPORTED DATABASE' }>> => {
    const queryApiFactory = await getServiceInstance<QueryApiFactoryService>(contextData, 'QueryApiFactoryService');
    return await queryApiFactory.getMadridQueryApi(caller).queryDataset<
        Array<{
            name: string;
            kind?: 'STANDARD' | 'IMPORTED DATABASE';
        }>
    >({
        linkedService,
        datasetType: adfModelTypeMap.snowflake.datasetType,
        query: isDatabase ? 'show databases' : 'SHOW STORAGE INTEGRATIONS',
        parameterInfo, // handle ls has parameters in ADF
        queryAlias: 'query',
        connectionProperties
    });
};

const listSQLBasedDatabase = async (
    contextData: IResourceContext,
    linkedService: AzureSqlDatabaseLinkedService,
    parameterInfo?: IQueryParameterInfo,
    caller?: FabricCaller | null
): Promise<Array<{ name: string; kind: 'STANDARD' | 'IMPORTED DATABASE' }>> => {
    const queryApiFactory = await getServiceInstance<QueryApiFactoryService>(contextData, 'QueryApiFactoryService');
    return await queryApiFactory.getMadridQueryApi(caller).queryDataset<
        Array<{
            name: string;
            kind: 'STANDARD' | 'IMPORTED DATABASE';
        }>
    >({
        linkedService,
        datasetType: adfModelTypeMap.azureSql.datasetType,
        query: 'SELECT name FROM sys.databases',
        // query: `SELECT name
        //         FROM sys.databases
        //         WHERE HAS_DBACCESS(name) = 1`,
        queryAlias: 'sqlReaderQuery',
        parameterInfo
    });
};

const listDatabases = async (
    contextData: IResourceContext,
    linkedService: LinkedService,
    parameterInfo?: IQueryParameterInfo,
    connectionProperties?: { [key: string]: string },
    caller?: FabricCaller | null
) => {
    let result;
    if (
        linkedService.tridentConnectionKind === ConnectionKind.SNOWFLAKE &&
        linkedService instanceof SnowflakeLinkedService
    ) {
        result = await listSnowflakeData(contextData, linkedService, undefined, parameterInfo, connectionProperties, caller);
    } else if (linkedService.tridentConnectionKind === ConnectionKind.SQL) {
        result = await listSQLBasedDatabase(contextData, linkedService as AzureSqlDatabaseLinkedService, parameterInfo, caller);
    } else {
        const queryApiFactory = await getServiceInstance<QueryApiFactoryService>(contextData, 'QueryApiFactoryService');
        const queryOptions = (
            linkedService.tridentConnectionKind === ConnectionKind.DATABRICKS ||
            (linkedService.tridentConnectionKind === ConnectionKind.DATABRICKS_MULTI_CLOUD && GlobalVariables.enableDatabricks)
        )
            ? { navigationLevel: 'catalog' }
            : undefined;
        result = await queryApiFactory.getMadridQueryApi(caller).getNavTableAsync(linkedService, parameterInfo, queryOptions);
    }
    return result.map((item) => ({
        label: item.name,
        value: item.name
    }));
};

const getDatabaseFromLinkedService = (linkedService: LinkedService, attributeName = 'databaseName'): string => {
    if (!(linkedService instanceof LinkedService)) {
        return undefined;
    }
    const lsModel = Metamodel.modelOf(linkedService);
    if (lsModel.hasAttribute(attributeName)) {
        return lsModel.attribute(attributeName).value(linkedService);
    }
    return undefined;
};

const isSalesforceV2BasedDataset = (datasetType: string): boolean => {
    return (
        datasetType === adfModelTypeMap.salesforceV2.datasetType ||
        datasetType === adfModelTypeMap.salesforceServiceCloudV2.datasetType
    );
};

const getDisplayNameFromDataset = (
    dataset: Dataset,
    datasetRegistryService: DatasetRegistryService
): string | undefined => {
    if (dataset == null) {
        return undefined;
    }

    if (isFileBasedTypeDataset(dataset, { dsSvc: datasetRegistryService })) {
        const _dataset = dataset as FormatDatasetV2;
        return _dataset.location?.getLocation()?.getFullPath();
    }

    if (dataset instanceof TabularTypeDataset) {
        return compact([dataset.schema, dataset.table || dataset.tableName]).join('.');
    }

    const datasetType = Metamodel.modelOf(dataset).name;
    if (datasetType === adfModelTypeMap.rest.datasetType) {
        return (dataset as RestResourceDataset).relativeUrl;
    }

    return undefined;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const DatasetUtils = {
    isFileBasedTypeDataset,
    isSqlBasedDataflowDataset,
    isSqlBasedDatasetType,
    isSqlBasedDataset,
    isSqlServer,
    listSnowflakeData,
    listDatabases,
    getDatabaseFromLinkedService,
    isSalesforceV2BasedDataset,
    getDisplayNameFromDataset
};

// Extracts an attribute value from a dataset.
export function getAttributeValueFromDataset<T = string>(
    dataset: Dataset | undefined,
    attributeName: string
): T | undefined {
    if (!dataset) return undefined;

    const dsModel = Metamodel.modelOf(dataset);
    if (dsModel?.hasAttribute(attributeName)) {
        const value = dsModel.attribute(attributeName).value(dataset);
        return (value ?? undefined) as T | undefined;
    }
    return undefined;
}
